"use client"
import { useCallback, useEffect, useRef, useState } from 'react'
import { useCookies } from 'react-cookie'
import { apiFetch } from '@/lib/apiFetch'

// 语音录音状态: 空闲 / 录音中 / 识别结果返回中
export type VoiceStatus = 'idle' | 'recording' | 'loading'

const COZE_WS_URL = process.env.NEXT_PUBLIC_COZE_WS_URL || 'wss://ws.coze.cn'
// token 获取方式对齐 ly-gpt(GET, Authorization=tokenHead+token, 取 code===0 的 data.data);
// 服务前缀由 apiFetch 按机构决定 (C 走 wenkoo-portal, 其余走 wenkoo-tenant)
const COZE_TOKEN_PATH = '/aiChat/chat/coze/getAccessToken'

// 参考 ly-gpt 的语音输入(Coze WebSocket 流式识别), 移植为 React hook
export function useVoice(onDone: (text: string) => void, options?: { tenantId?: string }) {
  const [cookies] = useCookies(['token'])
  const [status, setStatus] = useState<VoiceStatus>('idle')

  // Coze WsTranscriptionClient 实例
  const clientRef = useRef<any>(null)
  // 实时识别文本
  const textRef = useRef('')
  // 防止 completed 事件与兜底定时器重复收尾
  const sessionDoneRef = useRef(false)
  const finishTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // 避免 onDone 闭包过期
  const onDoneRef = useRef(onDone)
  useEffect(() => {
    onDoneRef.current = onDone
  }, [onDone])

  // 获取 Coze access token (与 ly-gpt 的 useCozeAccessToken 一致:
  // GET /wk-api/aiChat/chat/coze/getAccessToken, 鉴权头 Authorization=tokenHead+token,
  // 响应 code===0 时返回 data.data)
  const getAccessToken = useCallback(async () => {
    const tokenHead = cookies.token?.tokenHead || ''
    const tokenValue = cookies.token?.token || ''
    const authorization = `${tokenHead}${tokenValue}`
    const res = await apiFetch(COZE_TOKEN_PATH, {
      tenantId: options?.tenantId,
      authorization,
      headers: { 'Accept-Language': 'zh-CN' },
    })
    const data = await res.json()
    if (data?.code !== 0 || !data?.data) {
      return Promise.reject(data)
    }
    return data.data as string
  }, [cookies.token, options?.tenantId])

  // 建立 WebSocket 连接(不开麦)
  const prepare = useCallback(async () => {
    if (clientRef.current) return clientRef.current
    const { WsTranscriptionClient } = await import('@coze/api/ws-tools')
    const token = await getAccessToken()
    const instance = new WsTranscriptionClient({
      token,
      baseWsURL: COZE_WS_URL,
      allowPersonalAccessTokenInBrowser: true,
      audioCaptureConfig: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
    })

    // 流式中间结果
    instance.on('transcriptions.message.update', (data: any) => {
      textRef.current = data?.data?.content ?? textRef.current
    })
    // stop 后服务端确认的最终结果
    instance.on('transcriptions.message.completed', (data: any) => {
      if (data?.data?.content) textRef.current = data.data.content
      finishVoice()
    })

    // start() 内部会自动建连, 此处仅创建实例并注册监听
    clientRef.current = instance
    return instance
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getAccessToken])

  // 统一收尾: 触发 onDone, 销毁连接
  const finishVoice = useCallback(() => {
    if (sessionDoneRef.current) return
    sessionDoneRef.current = true
    if (finishTimerRef.current) clearTimeout(finishTimerRef.current)
    setStatus('idle')

    const text = textRef.current
    textRef.current = ''

    // 销毁本次连接, 下次录音重新建连
    clientRef.current?.destroy?.()
    clientRef.current = null

    if (text) onDoneRef.current(text)
  }, [])

  // 点击开始录音
  const startRecord = useCallback(async () => {
    try {
      sessionDoneRef.current = false
      textRef.current = ''
      await prepare()
      await clientRef.current?.start()
      setStatus('recording')
    } catch (err) {
      console.error('[voice] start error', err)
      setStatus('idle')
      clientRef.current?.destroy?.()
      clientRef.current = null
      return Promise.reject(err)
    }
  }, [prepare])

  // 再次点击结束录音并提交, 等待最终识别结果
  const stopRecord = useCallback(() => {
    setStatus('loading')
    clientRef.current?.stop?.()
    if (finishTimerRef.current) clearTimeout(finishTimerRef.current)
    // 兜底: 1.5s 内未收到 completed 事件则强制收尾
    finishTimerRef.current = setTimeout(finishVoice, 1500)
  }, [finishVoice])

  // 切换录音状态
  const toggleRecord = useCallback(async () => {
    if (status === 'idle') {
      await startRecord()
    } else if (status === 'recording') {
      stopRecord()
    }
  }, [status, startRecord, stopRecord])

  // 卸载时清理
  useEffect(() => {
    return () => {
      if (finishTimerRef.current) clearTimeout(finishTimerRef.current)
      clientRef.current?.destroy?.()
      clientRef.current = null
    }
  }, [])

  return {
    status,
    toggleRecord,
  }
}
